import { Spacing } from '@persona-id/design-tokens';
import { useContext } from 'react';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import Markdown from 'shared/components/Markdown';
import styled, { ThemeContext } from 'styled-components';

import IconCamera from 'images/icons/inquiry/camera.svg';
import ImageDocument from 'images/pictographs/document.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import CustomAssetWithFallback from 'inquiry/components/CustomAssetWithFallback';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import VerificationHero from 'inquiry/components/VerificationHero';
import { useStepDocumentStyleContext } from 'inquiry/context/StepDocumentStyleContext';
import { colorCss } from 'lib/inquiryStyles';
import { StepDocumentConfig } from 'lib/interfaces/StepsConfig';
import { PictographPosition } from 'lib/interfaces/ThemeSerialized';
import { assertNever } from 'lib/utils/types';
import ButtonInputCapture from 'verify/components/ButtonInputCapture';
import CenteredPictograph from 'verify/components/CenteredPictograph';
import CustomizablePictograph from 'verify/components/CustomizablePictograph';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import {
  DocumentCaptureOptionDesktop,
  DocumentCaptureOptionMobile,
} from 'verify/lib/interfaces/CaptureOption';
import PromptOptions from 'verify/lib/interfaces/PromptOptions';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${Spacing[6]};
`;

const StyledCustomizablePictograph = styled(CustomizablePictograph)`
  width: 100%;
`;

interface DocumentsPromptProps {
  allowedCaptureOptions: Array<DocumentCaptureOptionDesktop | DocumentCaptureOptionMobile>;
  acceptedFileTypes: StepDocumentConfig['accepted-file-types'];
  assets: StepDocumentConfig['assets']['prompt-page'];
  handleDeviceHandoff: () => void;
  handleDrop: (acceptedFiles: File[], rejectedFiles: FileRejection[], event: DropEvent) => void;
  handleDropRejected: (rejectedFiles: FileRejection[]) => void;
  handleInputCapture: (file: File) => void;
  handleSelectCamera: () => Promise<void>;
  isProcessing: PromptOptions | undefined;
  loading: boolean;
  localizations: StepDocumentConfig['localizations']['prompt-page'];
  multiFileSupported?: boolean;
}

/**
 * View rendered when no files have been uploaded to document.
 */
export default function DocumentsPrompt({
  allowedCaptureOptions,
  acceptedFileTypes,
  assets,
  handleDeviceHandoff,
  handleDrop,
  handleDropRejected,
  handleInputCapture,
  handleSelectCamera,
  isProcessing,
  loading,
  localizations,
  multiFileSupported,
}: DocumentsPromptProps) {
  const { documentPictographPosition } = useContext(ThemeContext);
  const { base } = useStepDocumentStyleContext();

  const pictographNode = (
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    <CenteredPictograph height={null} width={null} aria-label='document pictograph'>
      <CustomAssetWithFallback
        customImage={assets['document-pictograph']}
        alt='document pictograph'
      >
        <StyledCustomizablePictograph
          url='documentPictographUrl'
          height='documentPictographHeight'
          aria-label='document pictograph'
          vectorEffect='initial'
          primaryColor={colorCss(base?.fill.color)}
          highlightColor={colorCss(base?.fill.highlightColor)}
          backgroundColor={colorCss(base?.fill.backgroundColor)}
          strokeColor={colorCss(base?.stroke.color)}
        >
          <VerificationHero backgroundColor={colorCss(base?.fill.highlightColor)}>
            <ImageDocument height={80} width='100%' />
          </VerificationHero>
        </StyledCustomizablePictograph>
      </CustomAssetWithFallback>
    </CenteredPictograph>
  );

  return (
    <Container data-test='documents-prompt'>
      <CustomAssetWithFallback customImage={assets['header-pictograph']} alt='header pictograph' />
      {documentPictographPosition === PictographPosition.Start && pictographNode}
      <Header>
        <ThemedTitle styles={base?.titleStyle}>
          <Markdown>{localizations.title}</Markdown>
        </ThemedTitle>
        <ThemedParagraph data-test='documents-prompt__capture-options' styles={base?.textStyle}>
          <Markdown>
            {allowedCaptureOptions.length > 0
              ? localizations.prompt
              : localizations['no-capture-options-prompt']}
          </Markdown>
        </ThemedParagraph>
      </Header>
      {documentPictographPosition === PictographPosition.Center && pictographNode}
      <PositionableButtonContainer>
        <CaptureOptions
          allowedCaptureOptions={allowedCaptureOptions}
          acceptedFileTypes={acceptedFileTypes}
          handleDeviceHandoff={handleDeviceHandoff}
          handleDrop={handleDrop}
          handleDropRejected={handleDropRejected}
          handleInputCapture={handleInputCapture}
          handleSelectCamera={handleSelectCamera}
          isProcessing={isProcessing}
          loading={loading}
          localizations={localizations}
          multiFileSupported={multiFileSupported}
        />
      </PositionableButtonContainer>
    </Container>
  );
}

interface CaptureOptionsProps
  extends Pick<
    DocumentsPromptProps,
    | 'allowedCaptureOptions'
    | 'acceptedFileTypes'
    | 'handleDeviceHandoff'
    | 'handleDrop'
    | 'handleDropRejected'
    | 'handleInputCapture'
    | 'handleSelectCamera'
    | 'isProcessing'
    | 'loading'
    | 'localizations'
    | 'multiFileSupported'
  > {}

function CaptureOptions({
  allowedCaptureOptions,
  acceptedFileTypes,
  handleDeviceHandoff,
  handleDrop,
  handleDropRejected,
  handleInputCapture,
  handleSelectCamera,
  isProcessing,
  loading,
  localizations,
  multiFileSupported,
}: CaptureOptionsProps) {
  return (
    <CaptureOptionsContainer id='document-prompt__view'>
      {allowedCaptureOptions.map((option, index) => (
        <OptionBlock
          key={option}
          isPrimary={index === 0}
          option={option}
          handleDeviceHandoff={handleDeviceHandoff}
          handleDrop={handleDrop}
          handleDropRejected={handleDropRejected}
          handleInputCapture={handleInputCapture}
          handleSelectCamera={handleSelectCamera}
          isProcessing={isProcessing}
          loading={loading}
          localizations={localizations}
          multiFileSupported={multiFileSupported}
          acceptedFileTypes={acceptedFileTypes}
        />
      ))}
    </CaptureOptionsContainer>
  );
}

interface OptionBlockProps
  extends Pick<
    DocumentsPromptProps,
    | 'handleDeviceHandoff'
    | 'handleDrop'
    | 'handleDropRejected'
    | 'handleInputCapture'
    | 'handleSelectCamera'
    | 'isProcessing'
    | 'loading'
    | 'localizations'
    | 'multiFileSupported'
    | 'acceptedFileTypes'
  > {
  isPrimary: boolean;
  option: DocumentCaptureOptionDesktop | DocumentCaptureOptionMobile;
}

function OptionBlock({
  handleDeviceHandoff,
  handleDrop,
  handleDropRejected,
  handleInputCapture,
  handleSelectCamera,
  isPrimary,
  isProcessing,
  loading,
  localizations,
  option,
  multiFileSupported,
  acceptedFileTypes,
}: OptionBlockProps) {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleDrop,
    onDropRejected: handleDropRejected,
    multiple: !!multiFileSupported,
    accept: acceptedFileTypes as string[],
  });

  const { buttons: buttonStyles } = useStepDocumentStyleContext();
  const buttonType = isPrimary ? ButtonType.Primary : ButtonType.Secondary;
  const buttonDisabled = isProcessing != null || loading;

  switch (option) {
    case 'web_camera':
      return (
        <OptionButton
          id='document-prompt__button--web-camera'
          data-test='document-prompt__button--web-camera'
          buttonType={buttonType}
          disabled={buttonDisabled}
          styles={buttonStyles?.[buttonType]}
          onClick={handleSelectCamera}
          isPending={isProcessing === PromptOptions.SelectCamera}
        >
          <CaptureButtonContent>{localizations['btn-capture']}</CaptureButtonContent>
        </OptionButton>
      );

    case 'mobile_camera':
      // Note: This uploads immediately and skips check view. Maybe change?
      return (
        <ButtonInputCapture
          id='document-prompt__button--mobile-camera'
          data-test='document-prompt__button--mobile-camera'
          buttonType={buttonType}
          disabled={buttonDisabled}
          styles={buttonStyles?.[buttonType]}
          handleSelectImage={handleInputCapture}
          isPending={isProcessing === PromptOptions.UploadImage}
        >
          <CaptureButtonContent>{localizations['btn-capture']}</CaptureButtonContent>
        </ButtonInputCapture>
      );

    case 'upload':
      // Note: This uploads immediately and skips check view.
      return (
        <OptionButton
          id='document-prompt__button--upload'
          data-test='document-prompt__button--upload'
          buttonType={buttonType}
          disabled={buttonDisabled}
          styles={buttonStyles?.[buttonType]}
          {...getRootProps()}
        >
          <input
            {...getInputProps()}
            data-cy='file-upload'
            data-test='document-prompt__input--upload'
          />
          {localizations['btn-upload']}
        </OptionButton>
      );

    case 'device_handoff':
      return (
        <OptionButton
          id='document-prompt__button--device-handoff'
          data-test='document-prompt__button--device-handoff'
          buttonType={buttonType}
          disabled={buttonDisabled}
          styles={buttonStyles?.[buttonType]}
          onClick={handleDeviceHandoff}
          isPending={isProcessing === PromptOptions.DeviceHandoff}
        >
          {localizations['btn-device-handoff']}
        </OptionButton>
      );

    default:
      assertNever(option);
  }
}

const CaptureOptionsContainer = styled.div`
  width: 100%;
`;

const OptionButton = styled(Button)`
  margin-bottom: ${Spacing[4]};
  :last-of-type {
    margin-bottom: 0;
  }
`;

const ImgCamera = styled(IconCamera)`
  vertical-align: bottom;

  path[stroke] {
    stroke: ${(props) => props.theme.colorFontButtonPrimary};
  }
`;

interface CaptureButtonContentProps {
  children: string | undefined;
}

const CaptureButtonContent = ({ children }: CaptureButtonContentProps) => {
  if (children) {
    return <span>{children}</span>;
  } else {
    return <ImgCamera data-test='document-prompt__image--camera' />;
  }
};
